import { Button, Group, Loader, rem, Text, Tooltip, useMantineTheme } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconEdit, IconExternalLink, IconEye, IconEyeOff, IconTrash, IconX } from "@tabler/icons-react";
import { UsersSearchRepository } from "shared/components/organization/users/data";
import { RoleAssignModal } from "shared/components/organization/users/RoleAssignModal";
import { useColorMode } from "shared/hooks/useColorMode";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { runAsync, to } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { UserStatus } from "src/gql/graphql";
import { container } from "tsyringe";

type FatProps = {
    selectedUsers: { id: string; status: UserStatus }[];
    onClear: () => void;
};

export const FloatingActionToolbar = ({ selectedUsers, onClear }: FatProps) => {
    const theme = useMantineTheme();
    const { isDark } = useColorMode();
    const [opened, { toggle }] = useDisclosure(true);

    const deleteUser = useGraphqlMutation(
        graphql(`
            mutation FatDeleteUser($userId: UUID!) {
                archiveUser(userId: $userId)
            }
        `),
    );

    const resendInvites = useGraphqlMutation(
        graphql(`
            mutation FatResendInvites($userIds: [UUID!]!) {
                resendUserInvites(userIds: $userIds) {
                    id
                }
            }
        `),
    );

    const repo = container.resolve(UsersSearchRepository);

    return (
        <Group
            justify="center"
            p="xs"
            gap={0}
            pos="absolute"
            bottom={rem(20)}
            w="fit-content"
            style={{
                left: 0,
                right: 0,
                marginInline: "auto",
                backgroundColor: isDark ? theme.colors.dark[4] : theme.colors.gray[2],
                opacity: opened ? "95%" : "50%",
                transition: "opacity 1.5s",
                borderRadius: "0.5rem",
                overflow: "hidden",
                flexWrap: "nowrap",
            }}
        >
            <Group justify="center" ml={opened ? 0 : -482} style={{ transition: "margin-left 1s" }}>
                <Button
                    leftSection={<IconTrash size={16} />}
                    variant="subtle"
                    onClick={() =>
                        modals.openConfirmModal({
                            title: `Delete ${selectedUsers.length} User(s)?`,
                            centered: true,
                            children: (
                                <Text>Are you sure you want to permanently delete the selected user(s)? This action cannot be undone.</Text>
                            ),
                            labels: { confirm: `Delete ${selectedUsers.length} user(s)`, cancel: "Cancel" },
                            onConfirm: () => {
                                runAsync(async () => {
                                    await Promise.allSettled(
                                        selectedUsers.map((it) => deleteUser.mutateAsync({ userId: it.id }).then(() => repo.remove(it.id))),
                                    ).then((res) => {
                                        const successes = res.filter((it) => it.status == "fulfilled");
                                        if (successes.length == res.length) {
                                            onClear();
                                            notify.show.success({ message: "Users archived" });
                                        } else if (successes.length == 0) notify.show.warn({ message: "Error archiving user(s)" });
                                        else
                                            notify.show.warn({
                                                message: `Error archiving ${res.length - successes.length} of ${res.length} users`,
                                            });
                                    });
                                });
                            },
                        })
                    }
                >
                    Remove User{selectedUsers.length > 1 ? "s" : ""}
                </Button>
                <Button
                    leftSection={<IconEdit size={16} />}
                    variant="subtle"
                    onClick={() =>
                        modals.open({
                            title: "Assign Role",
                            centered: true,
                            children: <RoleAssignModal userIds={selectedUsers.map(to("id"))} />,
                        })
                    }
                >
                    Assign Roles
                </Button>
                <Button maw={150} w={150} leftSection={<IconX size={16} />} onClick={onClear} variant="subtle">
                    {selectedUsers.length} selected
                </Button>
                {selectedUsers.some((it) => it.status == UserStatus.Invited) && (
                    <Button
                        leftSection={resendInvites.isPending ? <Loader size={16} /> : <IconExternalLink size={16} />}
                        disabled={resendInvites.isPending}
                        onClick={() =>
                            void resendInvites
                                .mutateAsync({
                                    userIds: selectedUsers.filter((it) => it.status == UserStatus.Invited).map(to("id")),
                                })
                                .then(() => notify.show.success("Resent invites"))
                        }
                        variant="subtle"
                    >
                        Resend Invite
                    </Button>
                )}
                {opened ? (
                    <Tooltip label="Hide">
                        <Button variant="subtle" p="xs" onClick={toggle} aria-label="Hide floating toolbar">
                            <IconEyeOff size={16} />
                        </Button>
                    </Tooltip>
                ) : (
                    <Tooltip label="Show">
                        <Button variant="subtle" p="xs" onClick={toggle} aria-label="Show floating toolbar">
                            <IconEye size={16} />
                        </Button>
                    </Tooltip>
                )}
            </Group>
        </Group>
    );
};
